<template>
  <ModalComp v-model:isVisible="visible" :title="title" size="lg">
    <template #body>
      <p class="mb-3 font-semibold">{{ original?.reference }}</p>
      <p v-if="action === 'remove'" class="text-sm">Retirer ce candidat ? L’article reste en rupture.</p>
      <template v-else>
        <form v-if="action === 'product'" class="mb-3 flex gap-2" @submit.prevent="searchProducts">
          <input v-model="search" :disabled="busy" placeholder="Référence du nouveau produit" class="min-w-0 flex-1 rounded border p-2" aria-label="Référence du nouveau produit" />
          <button type="submit" :disabled="busy || loading" class="rounded border px-3 py-2">Rechercher</button>
        </form>
        <ReplacementProductPicker v-if="action === 'product'" :products="choices" :selected-ids="[selection.id_produit_remplacement]" :selections="[selection]" :loading="loading" :disabled="busy" @choose="choose" />
        <p v-if="loading" class="my-2 text-sm text-indigo-700" role="status">Chargement des produits…</p>
        <p class="my-3 text-sm font-semibold">Quantité recommandée<template v-if="selected"> · {{ selected.reference }}</template></p>
        <label v-if="selected && selected.vendu_par !== 'tailles'" class="block text-sm">Quantité de paquets
          <input v-model.number="selection.quantite_pack" type="number" min="1" max="10000" :disabled="busy || loading" class="ml-2 w-24 rounded border p-2" @input="updatePacks" />
        </label>
        <p v-if="selected && selected.vendu_par !== 'tailles'" class="mt-2 text-sm text-gray-600">0 = taille non souhaitée. Modifier les paquets réinitialise les quantités.</p>
        <div class="mt-3 flex flex-wrap gap-3">
          <label v-for="size in sizes" :key="size.index" class="text-sm">{{ size.label }}
            <input :value="selection.quantites[size.index]" type="number" min="0" :max="selected?.vendu_par === 'tailles' ? 10000 : Math.min(10000, (selected?.pack[size.index] || 0) * selection.quantite_pack)" :disabled="busy || loading" class="mt-1 block w-20 rounded border p-2" @input="updateSizeQuantity(size.index, $event)" />
          </label>
        </div>
        <p v-if="quoting" class="mt-3 text-sm text-indigo-700" role="status">Calcul du devis…</p>
        <ReplacementComparison v-if="preview.length" :lines="preview" class="mt-3" />
      </template>
      <p v-if="error" role="alert" class="mt-3 rounded bg-red-50 p-2 text-sm text-red-700">{{ error }}</p>
    </template>
    <template #footer>
      <div class="w-full space-y-3">
        <label class="flex items-start gap-2 text-sm"><input v-model="confirmed" type="checkbox" :disabled="busy || loading" class="mt-1" /><span>{{ action === 'remove' ? 'Je confirme le retrait.' : 'Je confirme la disponibilité et les écarts de prix à ma charge.' }}</span></label>
        <div class="flex justify-end gap-2">
          <button type="button" :disabled="busy" class="rounded border px-3 py-2" @click="visible = false">Fermer</button>
          <button type="button" :disabled="busy || loading || quoting || !confirmed || (action !== 'remove' && (!valid || !quoteHash))" class="rounded bg-indigo-600 px-3 py-2 text-white disabled:opacity-50" @click="save">{{ busy ? 'Enregistrement…' : action === 'remove' ? 'Annuler ce candidat' : 'Enregistrer' }}</button>
        </div>
      </div>
    </template>
  </ModalComp>
</template>
<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue';
import ModalComp from '@/components/ui/ModalComp.vue';
import ReplacementProductPicker from './ReplacementProductPicker.vue';
import ReplacementComparison from './ReplacementComparison.vue';
import { changeReplacementProduct, fetchReplacementProducts, previewReplacement, removeReplacementCandidate, replacementLineProducts, updateReplacementQuantity } from '@/api/remplacements';
import type { ReplacementAppliedProduct, ReplacementLine, ReplacementProduct, ReplacementProposal, ReplacementSelection } from '@/api/remplacements';

type Action = 'quantity' | 'remove' | 'product';
const emit = defineEmits<{ saved: [] }>();
const visible = ref(false), busy = ref(false), loading = ref(false), quoting = ref(false), confirmed = ref(false);
const action = ref<Action>('quantity');
const proposal = ref<ReplacementProposal | null>(null);
const line = ref<ReplacementLine | null>(null);
const original = ref<ReplacementAppliedProduct | null>(null);
const selection = ref<ReplacementSelection>({ id_ligne_origine: 0, id_produit_remplacement: 0, quantites: Array(12).fill(0), quantite_pack: 1 });
const products = ref<ReplacementProduct[]>([]), choices = ref<ReplacementProduct[]>([]);
const search = ref(''), error = ref(''), quoteHash = ref('');
const preview = ref<ReplacementLine[]>([]);
let revision = 0, quoteRevision = 0, searchRevision = 0;
let timer: ReturnType<typeof window.setTimeout> | null = null;
const title = computed(() => action.value === 'quantity' ? 'Modifier la quantité recommandée' : action.value === 'remove' ? 'Annuler le candidat' : 'Changer le produit recommandé');
const selected = computed(() => products.value.find(product => product.id_produit === selection.value.id_produit_remplacement));
const sizes = computed(() => selected.value?.tailles.flatMap((label, index) => selected.value?.vendu_par === 'tailles'
  ? label ? [{ label, index }] : [] : selected.value?.pack[index] ? [{ label: label || 'Assortiment', index }] : []) || []);
const valid = computed(() => {
  const product = selected.value, value = selection.value;
  if (!product || value.quantites.length !== 12 || value.quantites.some(q => !Number.isSafeInteger(q) || q < 0 || q > 10000)) return false;
  const count = value.quantites.reduce((sum, q) => sum + q, 0);
  return count > 0 && count <= 10000 && Number.isSafeInteger(value.quantite_pack) && value.quantite_pack >= 1 && value.quantite_pack <= 10000 &&
    (product.vendu_par === 'tailles' ? count >= product.qte_mini && value.quantites.every((q, i) => !q || Boolean(product.tailles[i])) : value.quantites.every((q, i) => q <= product.pack[i] * value.quantite_pack)) &&
    (action.value !== 'product' || value.id_produit_remplacement !== original.value?.id_produit);
});
function stopQuote() { if (timer != null) window.clearTimeout(timer); timer = null; quoteRevision++; quoting.value = false; }
function invalidate() {
  stopQuote(); quoteHash.value = ''; preview.value = []; confirmed.value = false;
  if (visible.value && action.value !== 'remove' && !loading.value && valid.value) {
    quoting.value = true;
    timer = window.setTimeout(() => { void quote(); }, 300);
  }
}
function updateSizeQuantity(index: number, event: Event) {
  const input = event.target as HTMLInputElement;
  if (input.value === '' && !input.validity.badInput) input.value = '0';
  selection.value.quantites[index] = input.value === '' ? NaN : Number(input.value);
}
function updatePacks() { if (selected.value) selection.value.quantites = selected.value.pack.map(q => q * selection.value.quantite_pack); }
function choose(id: number) {
  if (id === selection.value.id_produit_remplacement) return;
  selection.value.id_produit_remplacement = id; selection.value.quantite_pack = 1;
  selection.value.quantites = Array(12).fill(0);
  if (selected.value?.vendu_par !== 'tailles') updatePacks();
}
async function searchProducts() {
  const owner = proposal.value, origin = line.value;
  if (!owner || !origin) return;
  const current = revision, request = ++searchRevision;
  loading.value = true; error.value = ''; stopQuote();
  try {
    const rows = await fetchReplacementProducts(owner.id_commande, search.value.trim(), origin.id_ligne_origine, true);
    if (current !== revision || request !== searchRevision || !visible.value) return;
    choices.value = rows.filter(product => product.id_produit !== original.value?.id_produit &&
      !replacementLineProducts(origin).some(candidate => candidate.id_produit !== original.value?.id_produit && candidate.id_produit === product.id_produit));
    const merged = new Map(products.value.map(product => [product.id_produit, product]));
    rows.forEach(product => merged.set(product.id_produit, product)); products.value = [...merged.values()];
  } catch (err) { if (current === revision) error.value = err instanceof Error ? err.message : 'Recherche impossible'; }
  finally { if (current === revision && request === searchRevision) { loading.value = false; invalidate(); } }
}
async function open(value: ReplacementProposal, origin: ReplacementLine, candidate: ReplacementAppliedProduct, operation: Action) {
  const current = ++revision; stopQuote();
  proposal.value = JSON.parse(JSON.stringify(value)); line.value = JSON.parse(JSON.stringify(origin)); original.value = JSON.parse(JSON.stringify(candidate));
  action.value = operation; error.value = ''; search.value = ''; quoteHash.value = ''; preview.value = []; confirmed.value = false;
  products.value = []; choices.value = [];
  selection.value = { id_ligne_origine: origin.id_ligne_origine, id_produit_remplacement: candidate.id_produit, quantites: [...candidate.quantites], quantite_pack: candidate.quantite_pack };
  visible.value = true; loading.value = operation !== 'remove';
  if (operation === 'remove') return;
  try {
    const rows = await fetchReplacementProducts(value.id_commande, candidate.reference, origin.id_ligne_origine, true);
    if (current !== revision || !visible.value) return;
    products.value = rows;
    if (!rows.some(product => product.id_produit === candidate.id_produit) && operation === 'quantity') error.value = 'Ce produit n’est plus disponible. Choisissez un autre candidat.';
  } catch (err) { if (current === revision) error.value = err instanceof Error ? err.message : 'Chargement impossible'; }
  finally { if (current === revision) { loading.value = false; invalidate(); } }
  if (current === revision && visible.value && operation === 'product') await searchProducts();
}
async function quote() {
  stopQuote();
  const owner = proposal.value, origin = line.value, previous = original.value;
  if (!valid.value || !owner || !origin || !previous || !visible.value) return;
  const request = quoteRevision, current = revision;
  quoting.value = true; error.value = '';
  const selections = replacementLineProducts(origin).map(candidate => candidate.id_produit === previous.id_produit
    ? { ...selection.value, quantites: [...selection.value.quantites] }
    : { id_ligne_origine: origin.id_ligne_origine, id_produit_remplacement: candidate.id_produit, quantites: [...candidate.quantites], quantite_pack: candidate.quantite_pack });
  try {
    const result = await previewReplacement({ id_commande: owner.id_commande, lignes: selections, stock_confirme: false }, true);
    if (current !== revision || request !== quoteRevision || !visible.value) return;
    quoteHash.value = result.quote_hash; preview.value = result.lignes;
  } catch (err) { if (current === revision && request === quoteRevision) error.value = err instanceof Error ? err.message : 'Calcul impossible'; }
  finally { if (current === revision && request === quoteRevision) quoting.value = false; }
}
async function save() {
  if (busy.value || loading.value || quoting.value || !confirmed.value || !proposal.value || !line.value || !original.value || (action.value !== 'remove' && (!valid.value || !quoteHash.value))) return;
  busy.value = true; error.value = '';
  const target = { id_jet: proposal.value.id_jet, id_ligne_origine: line.value.id_ligne_origine, id_produit: original.value.id_produit };
  const context = { id_commande: proposal.value.id_commande, version: proposal.value.version };
  const quantity = { ...context, quantites: [...selection.value.quantites], quantite_pack: selection.value.quantite_pack, stock_confirme: true, quote_hash: quoteHash.value };
  try {
    if (action.value === 'remove') await removeReplacementCandidate(target, context);
    else if (action.value === 'quantity') await updateReplacementQuantity(target, quantity);
    else await changeReplacementProduct(target, { ...quantity, id_produit_remplacement: selection.value.id_produit_remplacement });
    visible.value = false; emit('saved');
  } catch (err) { error.value = err instanceof Error ? err.message : 'Modification impossible'; confirmed.value = false; }
  finally { busy.value = false; }
}
watch(() => JSON.stringify(selection.value), invalidate, { flush: 'sync' });
watch(visible, value => { if (!value) { revision++; stopQuote(); loading.value = false; } });
onUnmounted(stopQuote);
defineExpose({ open });
</script>
